'use client'
import { useAIFeatureEnableManager } from '@/src/hooks/AIFeatureEnableManager'
import dynamic from 'next/dynamic'
import { usePathname } from 'next/navigation'
import { useLocale } from 'next-intl'

import useClevertapForSrp from '@/src/hooks/analytics/clevertap/srp'
import { useThemedToast } from '@/src/hooks/ui/useThemedToast'

import { RecentSearchListItem } from '@/src/hooks/srp/recentSearch'
import { useHomePageSearchManager } from './hooks/useHomePageSearchManager'
import useHomepageGtmEvent from '@/src/hooks/analytics/gtm/homepage'
import useClevertapForHomepage from '@/src/hooks/analytics/clevertap/homepage'
import { useEffect } from 'react'

// Dynamic imports for code splitting
const MobileSearchForm = dynamic(
    () => import(/* webpackChunkName: "mobile-search-form" */ './MobileSearchForm'),
    {
        ssr: true
    }
)

const DesktopSearchForm = dynamic(
    () => import(/* webpackChunkName: "desktop-search-form" */ './DesktopSearchForm'),
    {
        ssr: true
    }
)

interface SearchFormProps {
    isMobile: boolean
    serverRecentSearches: RecentSearchListItem[]
    hideMulticity?: boolean
    fromParam?: any[] | null
    toParam?: any[] | null
    mobileMode?: 'search' | 'ai'
}

const SearchForm: React.FC<SearchFormProps> = ({
    isMobile,
    serverRecentSearches,
    hideMulticity,
    fromParam,
    toParam,
    mobileMode
}) => {
    // Common business logic
    const searchManager = useHomePageSearchManager({ maxSegments: 6, serverRecentSearches, fromParam, toParam })
    const { showError } = useThemedToast()
    const { triggerSrpEvent } = useClevertapForSrp()
    const { pushPageViewEvent } = useHomepageGtmEvent()
    const { triggerHomepageLoadEvent } = useClevertapForHomepage()
    const pathname = usePathname()
    const locale = useLocale()
    const { homeAISearch } = useAIFeatureEnableManager()
    const effectiveMobileMode = mobileMode ?? 'search'
    
    useEffect(() => {
        // Only send events if URL is /{locale} or /{locale}/flights
        const isHomepage = pathname === `/${locale}` || pathname === `/${locale}/flights`
        if (isHomepage) {
            pushPageViewEvent()
            triggerHomepageLoadEvent('Air_PageLoad')
        }
    }, [pathname, locale])

    // Conditional rendering based on server-side detection
    if (isMobile) {
        const isAIModeActive = homeAISearch && effectiveMobileMode === 'ai'
        return (
            <MobileSearchForm
                searchManager={searchManager}
                showError={showError}
                triggerSrpEvent={triggerSrpEvent}
                hideMulticity={hideMulticity}
                isAIModeActive={isAIModeActive}
            />
        )
    }

    return (
        <DesktopSearchForm 
            searchManager={searchManager} 
            showError={showError} 
            triggerSrpEvent={triggerSrpEvent}
            hideMulticity={hideMulticity}
        />
    )
}

export default SearchForm
